import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { Link as RouterLink, type To } from "react-router"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/sharp-regular-svg-icons"
import { faBoxArchive, faLayerGroup } from "@fortawesome/sharp-solid-svg-icons"
import SearchIcon from "@mui/icons-material/Search"
import UnarchiveIcon from "@mui/icons-material/Unarchive"
import {
  Box,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material"
import { GridColumnHeaderTitle, type GridColDef } from "@mui/x-data-grid-pro"
import { BiothreatSignalSource } from "@phc/common/gen/threatAnalyticsGen"
import { useRoles } from "../../../hooks/useRoles"
import { dayjsUTC } from "../../../utils/dates"
import { theme as appTheme, extraColors } from "../../../utils/theme"
import { LightTooltip } from "../../LocationDetails/LocationRisk/Shared"
import { useAlertCuration, type BiothreatSignal } from "../AlertCurationContext"
import {
  SOURCE_TYPE_TO_ICON,
  SOURCE_TYPE_TO_LABEL,
} from "../AlertCurationUtils"
import { ClusterTooltipContent } from "./ClusterMemberComponents"

const ScoreBadge = styled(Box, {
  shouldForwardProp: prop => prop !== "score",
})<{ score: number }>(({ score }) => ({
  ...appTheme.typography.small1Bold,
  backgroundColor:
    score > 0.9
      ? "rgba(41, 209, 159, 0.12)"
      : score > 0.7
        ? "rgba(41, 209, 159, 0.08)"
        : extraColors.translucent5,
  color: score > 0.7 ? extraColors.status.greenLight : extraColors.white,
  padding: "2px 0",
  borderRadius: "4px",
  textAlign: "center",
  width: "42px",
  height: "20px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${
    score > 0.9
      ? "rgba(41, 209, 159, 0.3)"
      : score > 0.7
        ? "rgba(41, 209, 159, 0.15)"
        : extraColors.translucent8
  }`,
}))

const ClusterBadge = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  background: extraColors.translucent5,
  padding: "4px 10px",
  borderRadius: "20px",
  border: `1px solid ${extraColors.translucent8}`,
  "& svg": {
    fontSize: "14px",
    opacity: 0.6,
  },
}))

const CellContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "centered" && prop !== "opacity",
})<{ centered?: boolean; opacity?: number }>(({ centered, opacity }) => ({
  display: "flex",
  alignItems: "center",
  height: "100%",
  width: "100%",
  ...(centered && { justifyContent: "center" }),
  ...(opacity !== undefined && { opacity }),
}))

const DescriptionCellContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "hasUrl",
})<{ hasUrl: boolean }>(({ hasUrl }) => ({
  display: "flex",
  alignItems: "center",
  height: "100%",
  width: "100%",
  gap: 1,
  cursor: hasUrl ? "pointer" : "default",
  textDecoration: "none",
  ...(hasUrl && {
    "&:hover": {
      textDecoration: "underline",
    },
  }),
}))

const SourceCellBadge = styled(ClusterBadge)(() => ({
  borderRadius: "4px",
  backgroundColor: extraColors.translucent5,
  width: "auto",
  border: `1px solid ${extraColors.dark20}`,
  padding: "2px 6px",
  color: extraColors.white,
}))

const ArchiveContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  px: 1,
}))

const DateLocationTypography = styled(Typography)(() => ({
  ...appTheme.typography.small1Bold,
  opacity: 0.8,
}))

const DiseaseTypography = styled(Typography)(() => ({
  ...appTheme.typography.small1Bold,
  fontWeight: 700,
  opacity: 0.9,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}))

const DescriptionTypography = styled(Typography)(() => ({
  fontSize: "0.75rem",
  fontWeight: 500,
  color: extraColors.white,
  opacity: 0.9,
  lineHeight: 1.3,
  py: 1,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
}))

const AnomalySearchIcon = styled(SearchIcon)(() => ({
  fontSize: 14,
  color: extraColors.status.navyLight,
  opacity: 0.8,
}))

const ArchiveIconButton = styled(IconButton)(() => ({
  transition: "all 0.2s",
  "&:hover": {
    opacity: 1,
    color: extraColors.status.redLight,
  },
  "&:disabled": {
    cursor: "not-allowed",
    pointerEvents: "all", // allow tooltip to trigger on disabled button
    "&:hover": {
      opacity: 0.5,
      color: extraColors.status.redLight,
    },
  },
}))

const ActionIconButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== "buttonColor",
})<{ buttonColor: string }>(({ buttonColor }) => ({
  color: buttonColor,
  padding: 0.5,
  "&:hover": {
    opacity: 0.8,
  },
}))

const HeaderWithTooltip: React.FC<{
  tooltipText: string
  columnWidth: number
  label: string
}> = ({ tooltipText, columnWidth, label }) => {
  return (
    <>
      <LightTooltip title={tooltipText} arrow placement="top-end">
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{
            fontSize: 16,
            color: extraColors.status.navyLight,
            paddingRight: 4,
          }}
        />
      </LightTooltip>
      <GridColumnHeaderTitle columnWidth={columnWidth} label={label} />
    </>
  )
}

export const getSharedAlertCandidateColumns = () => {
  return [
    {
      field: "modern_score",
      headerName: "AI SCORE",
      flex: 0,
      minWidth: 10,
      align: "center",
      headerAlign: "center",
      renderCell: params => <ScoreCell score={params.value as number} />,
      renderHeader: props => {
        const tooltipText =
          "An AI-assisted percentage that estimates how likely this cluster represents a biothreat at a confidence level that subject matter experts trust."
        return (
          <HeaderWithTooltip
            tooltipText={tooltipText}
            columnWidth={props.colDef.computedWidth}
            label={props.colDef.headerName || ""}
          />
        )
      },
    },
    {
      field: "threat_category",
      headerName: "TYPE",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: params => <TypeCell sourceType={params.row.source} />,
    },
    {
      field: "source_name",
      headerName: "SOURCE",
      width: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <SourceCell
          clusterId={params.row.cluster_id}
          clusterCount={params.row.cluster_count}
        />
      ),
    },
    {
      field: "publish_time",
      headerName: "DATE (UTC)",
      flex: 0,
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      renderCell: params => <DateCell dateString={params.value as string} />,
    },
    {
      field: "disease",
      headerName: "DISEASE",
      flex: 0,
      minWidth: 130,
      align: "left",
      headerAlign: "left",
      valueGetter: (_value, row) => {
        const codes = row.clean_disease_codes
        if (codes.length > 0) return codes.join(", ")

        const theme: string = row.pruned_themes[0] || "Unknown"
        const cleaned: string = theme.replace("TAX_DISEASE_", "")
        const firstPart: string | undefined = cleaned.split(",")[0]
        return (firstPart || "Unknown").replace(/_/g, " ")
      },
      renderCell: params => {
        const codes = params.row.clean_disease_codes
        const rawValue = codes[0] || String(params.value || "")
        const hasMore = codes.length > 1

        const content = (
          <DiseaseCellContent diseaseCode={rawValue} hasMore={hasMore} />
        )

        if (hasMore) {
          return <DiseaseTooltip codes={codes}>{content}</DiseaseTooltip>
        }

        return content
      },
    },
    {
      field: "resolved_location",
      headerName: "LOCATION",
      flex: 0,
      minWidth: 100,
      align: "left",
      headerAlign: "left",
      renderCell: params => (
        <LocationCell location={params.row.resolved_location} />
      ),
    },
    {
      field: "title",
      headerName: "DESCRIPTION",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: params => (
        <DescriptionCell
          title={String(params.value || "")}
          url={params.row.url}
          isAnomaly={
            params.row.source === BiothreatSignalSource.nwss &&
            !!params.row.search_term_id
          }
        />
      ),
      renderHeader: props => {
        const tooltipText =
          "The title of the most representative article from this cluster."
        return (
          <HeaderWithTooltip
            tooltipText={tooltipText}
            columnWidth={props.colDef.computedWidth}
            label={props.colDef.headerName || ""}
          />
        )
      },
    },
  ] satisfies GridColDef<BiothreatSignal>[]
}

export const ActionCell = <T extends { id: string }>({
  actions,
  row,
}: {
  actions: {
    onAction?: (row: T) => void
    to?: (row: T) => To
    icon: IconDefinition
    tooltip: string
    color: string
    shouldShow?: (row: T) => boolean
    iconSize?: number
  }[]
  row: T
}) => {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: "center",
      }}
    >
      {actions.map((action, idx) => {
        if (action.shouldShow && !action.shouldShow(row)) {
          return null
        }

        const iconSize = action.iconSize ?? 18
        const to = action.to?.(row)
        const linkProps = to
          ? ({
              component: RouterLink,
              to,
              target: "_blank",
              rel: "noopener noreferrer",
            } as const)
          : {}

        return (
          <Tooltip key={idx} title={action.tooltip} arrow placement="top">
            <ActionIconButton
              {...linkProps}
              size="small"
              buttonColor={action.color}
              onClick={e => {
                e.stopPropagation()
                if (!to) action.onAction?.(row)
              }}
            >
              <FontAwesomeIcon
                icon={action.icon}
                style={{ fontSize: iconSize, color: action.color }}
              />
            </ActionIconButton>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

const DiseaseTooltip: React.FC<{
  codes: string[]
  children: React.ReactElement
}> = ({ codes, children }) => {
  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          {codes.map(c => {
            const formatted = formatDiseaseForDisplay(c)
            return (
              <Typography
                key={c}
                variant="caption"
                sx={{
                  display: "block",
                }}
              >
                • {formatted}
              </Typography>
            )
          })}
        </Box>
      }
      arrow
      placement="top"
    >
      {children}
    </Tooltip>
  )
}

const DiseaseCellContent: React.FC<{
  diseaseCode: string
  hasMore: boolean
}> = ({ diseaseCode, hasMore }) => {
  return (
    <CellContainer>
      <DiseaseTypography>
        {formatDiseaseForDisplay(diseaseCode)}
        {hasMore && "..."}
      </DiseaseTypography>
    </CellContainer>
  )
}

const ScoreCell: React.FC<{
  score: unknown
}> = ({ score }) => {
  if (score == null) return null
  const scoreValue =
    typeof score === "number"
      ? score
      : typeof score === "string"
        ? parseFloat(score)
        : 0
  return (
    <CellContainer centered>
      <ScoreBadge score={scoreValue}>
        {(scoreValue * 100).toFixed(0)}%
      </ScoreBadge>
    </CellContainer>
  )
}

const TypeCell: React.FC<{ sourceType: BiothreatSignalSource }> = ({
  sourceType,
}) => {
  return (
    <Tooltip title={SOURCE_TYPE_TO_LABEL[sourceType]} arrow placement="top">
      <CellContainer centered opacity={0.6}>
        <FontAwesomeIcon
          icon={SOURCE_TYPE_TO_ICON[sourceType]}
          style={{ fontSize: 18 }}
        />
      </CellContainer>
    </Tooltip>
  )
}

const SourceCell: React.FC<{
  clusterId: string
  clusterCount: number
}> = ({ clusterId, clusterCount }) => {
  const clusterIcon = (
    <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 14, opacity: 1 }} />
  )

  if (!clusterCount) {
    return (
      <CellContainer centered>
        <SourceCellBadge>{clusterIcon}</SourceCellBadge>
      </CellContainer>
    )
  }

  return (
    <CellContainer>
      <Tooltip
        title={<ClusterTooltipContent clusterId={clusterId} />}
        arrow
        placement="right"
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: extraColors.dark,
              border: `1px solid ${extraColors.translucent8}`,
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              p: 0,
            },
          },
        }}
      >
        <CellContainer centered>
          <SourceCellBadge sx={{ gap: 0.5 }}>
            {clusterIcon}
            <Typography
              variant="small1Bold"
              color="inherit"
              sx={{
                lineHeight: "normal",
              }}
            >
              {clusterCount}
            </Typography>
          </SourceCellBadge>
        </CellContainer>
      </Tooltip>
    </CellContainer>
  )
}

const DateCell: React.FC<{ dateString: string }> = ({ dateString }) => {
  return (
    <CellContainer>
      <DateLocationTypography>
        {dayjsUTC(dateString).format("MM/DD/YY h:mm A")}
      </DateLocationTypography>
    </CellContainer>
  )
}

const LocationCell: React.FC<{ location?: string }> = ({ location }) => {
  return (
    <CellContainer>
      <DateLocationTypography>{location || ""}</DateLocationTypography>
    </CellContainer>
  )
}

const DescriptionCell: React.FC<{
  title: string
  url?: string
  isAnomaly: boolean
}> = ({ title, url, isAnomaly }) => {
  const hasUrl = !!url

  const anchorProps = hasUrl
    ? {
        component: "a" as const,
        href: url,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }
    : {}

  return (
    <DescriptionCellContainer {...anchorProps} hasUrl={hasUrl}>
      {isAnomaly && <AnomalySearchIcon />}
      <DescriptionTypography variant="body2">{title}</DescriptionTypography>
    </DescriptionCellContainer>
  )
}

export const ArchiveCell: React.FC<{
  clusterId: string
  isArchivedView: boolean
  triage: (payload: { ids: string[]; action: "archive" | "unarchive" }) => void
}> = ({ clusterId, isArchivedView, triage }) => {
  const { hasPHCAccessRole } = useRoles()
  const hasPHCRole = hasPHCAccessRole()
  const { signals } = useAlertCuration()

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!hasPHCRole) return
    const clusterMembers = signals.filter(s => s.cluster_id === clusterId)
    const ids = clusterMembers.map(m => m.id)

    triage({
      ids,
      action: isArchivedView ? "unarchive" : "archive",
    })
  }

  const titleText = hasPHCRole
    ? isArchivedView
      ? "Unarchive Cluster"
      : "Archive Cluster"
    : "Only PHC users can archive clusters"

  return (
    <ArchiveContainer>
      <ArchiveIconButton
        size="small"
        onClick={handleArchiveClick}
        disabled={!hasPHCRole}
        title={titleText}
      >
        <span style={{ cursor: !hasPHCRole ? "not-allowed" : "inherit" }}>
          {isArchivedView ? (
            <UnarchiveIcon sx={{ fontSize: 16 }} />
          ) : (
            <FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: 16 }} />
          )}
        </span>
      </ArchiveIconButton>
    </ArchiveContainer>
  )
}

/**
 * Formats disease names for display.
 * Preserves all-uppercase acronyms (e.g., "RSV", "HFMD") and capitalizes other disease names.
 */
const formatDiseaseForDisplay = (diseaseCode: string): string => {
  // Preserve all-uppercase acronyms (e.g., "RSV", "HFMD")
  if (diseaseCode === diseaseCode.toUpperCase()) {
    return diseaseCode
  }
  // Otherwise, capitalize first letter of each word
  return diseaseCode
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}
